import { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Topbar } from './Topbar';
import { Sidebar } from './Sidebar';
import { UndoBar } from './UndoBar';
import { useAuthStore } from '@/store/auth';
import { APP_BASE } from '@/lib/api-base';
import { expireIfStale } from '@/lib/session';

const SIDEBAR_WIDTH = 250;   // wireframe --sidebar-width
const TOPBAR_HEIGHT = 60;    // wireframe --header-height

/**
 * App shell — flex layout matching the legacy `.shell + .sb + .main`
 * structure. Three rules to remember:
 *
 *  1. Outer is `height: 100vh` + `overflow: hidden` so the page never
 *     scrolls at the window level. Only the main content area scrolls.
 *  2. Sidebar is a flex item with fixed width + `flex-shrink: 0`. Not
 *     position:fixed.
 *  3. Main column is `flex: 1` + `min-width: 0` (the latter is essential
 *     so wide tables don't push the column wider than its parent).
 *
 * On mobile (< md) the fixed sidebar would swallow most of the screen, so it
 * becomes an off-canvas Drawer opened from a hamburger in the topbar and
 * dismissed on navigation / backdrop tap.
 */
export function AppShell() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // A click into another page is the moment a stale session would otherwise
  // surface as a failed request — check the token first and go to sign-in.
  useEffect(() => { expireIfStale(); }, [location.pathname]);

  if (!isAuthed) {
    return <Navigate to={`${APP_BASE}/login`} replace state={{ from: location }} />;
  }

  // Employees land on /me. Blocking other admin paths is handled by
  // individual pages and the role-aware sidebar that hides them.
  const path = location.pathname;
  const isEss = path.startsWith(`${APP_BASE}/me`);
  if (role === 'EMPLOYEE' && !isEss) {
    return <Navigate to={`${APP_BASE}/me`} replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',          // never grow — single scroll surface
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {isMobile ? (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { border: 0, width: SIDEBAR_WIDTH } }}
        >
          <Sidebar width={SIDEBAR_WIDTH} inDrawer onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      ) : (
        <Sidebar width={SIDEBAR_WIDTH} />
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,             // CRITICAL — lets DataGrid etc. constrain to width
          minHeight: 0,
        }}
      >
        <Topbar
          height={TOPBAR_HEIGHT}
          showMenuButton={isMobile}
          onMenuClick={() => setMobileOpen(true)}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'auto',     // local horizontal scroll only when content actually exceeds
            // Always reserve the scrollbar's width, so a tall page (or tab)
            // and a short one lay out at the same width — otherwise the
            // header and cards jump sideways when switching between them.
            scrollbarGutter: 'stable',
            p: { xs: 1.5, sm: 2, md: 2.5 },
          }}
        >
          {/* Wireframe `.page-body` — page content sits on a white rounded
              card inset from the grey page background. Content fades in on
              each route change. */}
          <Box
            key={location.pathname}
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e1e5e8',
              minHeight: '100%',
              p: { xs: 1.75, sm: 2.5, md: 3 },
              minWidth: 0,
              animation: 'uknPageIn 260ms ease',
              '@keyframes uknPageIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'none' },
              },
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
      <UndoBar />
    </Box>
  );
}
